import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { OptionExternal } from '@/lib/v2/components/SelectExternalSearch';
import { GroupsIcon, SegmentsIcon, TagsIcon } from '@/lib/v2/icons/solid';
import { EcommerceIcon } from '@/lib/v2/icons/solid/EcommerceIcon';

import { useDebounce } from '@/src/application/hooks/useDebounce';
import { useCampaignsService } from '@/src/infrastructure/Protocol/Campaigns/useCampaignsService';
import { atomRecipientOptions } from '@/src/modules/CampaignsModule/atoms/actionsAtom';
import { ISMSFormInfo } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/components/SmsForm/hooks/useSmsForm';
import {
  EGroupAndListsTypeV1MapSms,
  EGroupAndListsTypeV1MapString,
  IAddresseesList,
  IAddresseesPayload,
} from '@/src/modules/CampaignsModule/interfaces/CampaignActions';

import { atomActionRecipients } from '@/modules/CampaignsModule/atoms/actionSMSAtom';
import { useActionSmsData } from '@/modules/CampaignsModule/hooks/useActionSmsData';

interface IRecipientsSmsProps {
  setValue?: UseFormSetValue<ISMSFormInfo>;
}

const ICONS_BY_TYPE: { [key: string]: JSX.Element } = {
  interest: <TagsIcon />,
  user: <GroupsIcon />,
  integration: <EcommerceIcon />,
  profile: <SegmentsIcon />,
};

const PAYLOAD_RECIPIENTS: IAddresseesPayload = {
  offset: 0,
  groupQuantity: 20,
  filterType: {
    user: true,
    profile: true,
    integration: true,
    interest: true,
  },
};

export const useRecipientsSms = ({ setValue }: IRecipientsSmsProps = {}) => {
  const [loading, setLoading] = useState(false);
  const { actionId } = useParams();

  const [recipientsOptions, setRecipientOptions] = useAtom(atomRecipientOptions);

  const [recipientListPayload, setRecipientListPayload] =
    useState<IAddresseesPayload>(PAYLOAD_RECIPIENTS);
  const [recipientsSearch, setRecipientsSearch] = useState<string>('');
  const [hasMoreRecipient, setHasMoreRecipient] = useState<boolean>(false);

  const campaignService = useCampaignsService();
  /* const { setInfoByFieldOnChange } = useSmsFormEvents({ actionId: Number(actionId) }); */
  const { setSmsRecipients } = useActionSmsData(Number(actionId));
  const recipientsSearchDebounced = useDebounce(recipientsSearch, 400);

  const actionRecipients = useAtomValue(atomActionRecipients);

  useEffect(() => {
    if (recipientsSearchDebounced.length === 0) return;

    const payloadOffset = {
      ...recipientListPayload,
      search: recipientsSearchDebounced,
      offset: 0,
    };

    void getRecipients(payloadOffset);

    setRecipientListPayload(payloadOffset);
    setHasMoreRecipient(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientsSearchDebounced]);

  //cspell:disable
  useEffect(() => {
    if (actionRecipients?.grupos?.length === 0) {
      setValue?.('recipients', []);
      return;
    }

    const recipients = actionRecipients?.grupos?.map((group) => {
      const groupType =
        EGroupAndListsTypeV1MapString[
          group.tipo.toUpperCase() as keyof typeof EGroupAndListsTypeV1MapString
        ];

      return {
        id: Number(group.id),
        name: String(group.nombre).trim() || '-',
        value: groupType,
        metadata: {
          icon: ICONS_BY_TYPE[groupType],
        },
      };
    });

    setValue?.('recipients', recipients || []);
  }, [actionRecipients, setValue]);
  //cspell:enable

  const getRecipientsOptions = useCallback(
    ({ addresseesList }: { addresseesList: IAddresseesList[] }): OptionExternal[] => {
      const addresseesOptions = addresseesList.map((address) => {
        return {
          id: Number(address.groupId),
          name: String(address.name).trim() || '-',
          value: address.type,
          metadata: {
            icon: ICONS_BY_TYPE[address.type],
            alt: `(${address.contactCount})`,
          },
        };
      });
      return addresseesOptions;
    },
    []
  );

  const getRecipients = useCallback(
    async (payload: IAddresseesPayload) => {
      if (!payload) return;

      setLoading(true);

      const newOffset = payload.offset || 0;
      const response = await campaignService.getAddressees(payload);
      const addresseesOptionsDropdown = getRecipientsOptions({ addresseesList: response });

      setRecipientOptions((prevOptions) =>
        newOffset === 0 ? addresseesOptionsDropdown : [...prevOptions, ...addresseesOptionsDropdown]
      );

      setHasMoreRecipient(response.length === payload.groupQuantity);

      setLoading(false);
    },
    [campaignService, getRecipientsOptions, setHasMoreRecipient, setRecipientOptions]
  );

  //cspell:disable
  const handleChangeRecipients = useCallback(
    (value: OptionExternal | OptionExternal[]) => {
      const selectedItems = Array.isArray(value) ? value : [value];
      const groups = selectedItems.map((item) => ({
        id: Number(item.id),
        tipo: EGroupAndListsTypeV1MapSms[item.value as keyof typeof EGroupAndListsTypeV1MapSms],
      }));

      const payload = {
        elementoId: Number(actionId),
        gruposId: groups,
      };

      void setSmsRecipients(payload);
    },
    [actionId, setSmsRecipients]
  );
  //cspell:enable

  const handleLoadLastRecipients = useCallback(
    async (option: OptionExternal) => {
      if (!hasMoreRecipient) return;

      if (option.id === recipientsOptions[recipientsOptions.length - 1].id) {
        const payloadOffset = {
          ...recipientListPayload,
          offset: recipientsOptions.length,
        };

        await getRecipients(payloadOffset);
        setRecipientListPayload(payloadOffset);
      }
    },
    [
      getRecipients,
      hasMoreRecipient,
      recipientListPayload,
      recipientsOptions,
      setRecipientListPayload,
    ]
  );

  const handleCloseRecipients = useCallback(() => {
    if (recipientsSearch.length === 0) return;

    const payloadOffset = { ...recipientListPayload, search: '', offset: 0 };

    void getRecipients(payloadOffset);
    setRecipientListPayload(payloadOffset);
    setHasMoreRecipient(true);
  }, [
    getRecipients,
    recipientListPayload,
    recipientsSearch.length,
    setHasMoreRecipient,
    setRecipientListPayload,
  ]);

  return {
    events: {
      getRecipients,
      handleChangeRecipients,
      handleCloseRecipients,
      handleLoadLastRecipients,
    },
    setters: {
      setRecipientsSearch,
    },
    state: {
      loading,
      recipientsOptions,
      recipientListPayload,
      recipientsSearchDebounced,
    },
  };
};
